import {
  postOpsAnalyticsAPI,
  postOpsAnalyticsBlobAPI,
} from "../../../../api/Networking";
import { APIBlobResponse } from "../../../../models/NetworkResponse";
import {
  RedemptionFilters,
  RedemptionGroupBy,
  RedemptionProductView,
} from "../../../../models/Dashboard/Redemption/RedemptionFilters";
import {
  RedemptionJourneyDrilldownResult,
  RedemptionProductDrilldownResult,
  RedemptionVariantMatrixResult,
} from "../../../../models/Dashboard/Redemption/RedemptionDrilldown";
import { RedemptionSummaryResult } from "../../../../models/Dashboard/Redemption/RedemptionSummary";

export default class RedemptionDashboardAPI {
  getSummary = async (
    filters: RedemptionFilters,
  ): Promise<RedemptionSummaryResult> => {
    try {
      const response = await postOpsAnalyticsAPI(
        "/v1/ops/redemptions/summary",
        filters,
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch ops redemptions summary.",
        );
      }
      return response.result as RedemptionSummaryResult;
    } catch (error) {
      throw error;
    }
  };

  getJourneyDrilldown = async (
    metricId: string,
    body: RedemptionFilters,
  ): Promise<RedemptionJourneyDrilldownResult> => {
    try {
      const response = await postOpsAnalyticsAPI(
        `/v1/ops/redemptions/drilldown/${encodeURIComponent(metricId)}`,
        body,
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch ops redemptions drilldown.",
        );
      }
      const result = response.result as RedemptionJourneyDrilldownResult & {
        stageId?: string;
        stats?: unknown;
        table?: unknown;
      };
      // Some envelopes nest under result.data; accept a flat data payload too.
      if (result.data?.stageId || result.data?.table) {
        return result as RedemptionJourneyDrilldownResult;
      }
      if (result.stageId || result.stats || result.table) {
        const payload = result as unknown as Record<string, unknown>;
        const { meta, ...data } = payload;
        return {
          meta: (meta || {}) as RedemptionJourneyDrilldownResult["meta"],
          data: data as unknown as RedemptionJourneyDrilldownResult["data"],
        };
      }
      return result as RedemptionJourneyDrilldownResult;
    } catch (error) {
      throw error;
    }
  };

  getProductDrilldown = async (
    productId: number,
    body: RedemptionFilters,
  ): Promise<RedemptionProductDrilldownResult> => {
    try {
      const response = await postOpsAnalyticsAPI(
        `/v1/ops/redemptions/product/${productId}/drilldown`,
        body,
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch ops redemptions drilldown.",
        );
      }
      return response.result as RedemptionProductDrilldownResult;
    } catch (error) {
      throw error;
    }
  };

  getVariantMatrix = async (
    body: RedemptionFilters,
  ): Promise<RedemptionVariantMatrixResult> => {
    try {
      const response = await postOpsAnalyticsAPI(
        "/v1/ops/redemptions/variant-matrix",
        body,
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch ops redemptions variant matrix.",
        );
      }
      return response.result as RedemptionVariantMatrixResult;
    } catch (error) {
      throw error;
    }
  };

  exportJourneyCsv = async (
    metricId: string,
    filters: RedemptionFilters,
  ): Promise<APIBlobResponse> => {
    try {
      return await postOpsAnalyticsBlobAPI(
        `/v1/ops/redemptions/drilldown/${encodeURIComponent(metricId)}`,
        {
          ...filters,
          format: "csv",
        },
      );
    } catch (error) {
      throw error;
    }
  };

  exportProductCsv = async (
    productId: number,
    filters: RedemptionFilters,
    view: RedemptionProductView,
  ): Promise<APIBlobResponse> => {
    try {
      return await postOpsAnalyticsBlobAPI(
        `/v1/ops/redemptions/product/${productId}/drilldown`,
        {
          ...filters,
          view,
          format: "csv",
        },
      );
    } catch (error) {
      throw error;
    }
  };

  exportVariantMatrixCsv = async (
    filters: RedemptionFilters,
    groupBy: RedemptionGroupBy,
  ): Promise<APIBlobResponse> => {
    try {
      return await postOpsAnalyticsBlobAPI("/v1/ops/redemptions/variant-matrix", {
        ...filters,
        group_by: groupBy,
        format: "csv",
      });
    } catch (error) {
      throw error;
    }
  };
}
